import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ArrowRight, Calendar, FileText, CreditCard, Users, Video, Sparkles, Shield, BellRing, BarChart3, Activity, Briefcase } from "lucide-react"
import MarketingLayout from "@/components/marketing/MarketingLayout"
import { Button } from "@/components/ui/button"
import { isEnabled, filterBullets } from "@/lib/feature-flags"
import { formatRon } from "@/lib/format"

const SECTION_ICONS = [Calendar, FileText, CreditCard, Users, Video, Sparkles, Activity, Briefcase]
const HIGHLIGHT_ICONS = [Shield, BellRing, BarChart3]
const CALENDAR_SLOTS = [3, 7, 12, 16, 21, 28]

export default function Features() {
  const { t } = useTranslation()
  const sectionsRaw = t("features.sections", { returnObjects: true })
  const stats = t("features.stats", { returnObjects: true })
  const highlightsRaw = t("features.highlights", { returnObjects: true })
  const sectionList = (Array.isArray(sectionsRaw) ? sectionsRaw : []).map((s, i) => ({
    ...s,
    icon: SECTION_ICONS[i] || Calendar,
  }))
  const highlights = Array.isArray(highlightsRaw) ? highlightsRaw : []

  if (process.env.NODE_ENV === "development" && sectionList.length === 0) {
    console.warn("[i18n] features.sections is empty — check locale files")
  }

  return (
    <MarketingLayout seoKey="features">
      <section className="pt-36 pb-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-5">{t("features.eyebrow")}</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-medium leading-[0.95]">{t("features.title")}</h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">{t("features.subtitle")}</p>
        </div>
      </section>

      <section className="border-y border-border bg-muted/20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {(Array.isArray(stats) ? stats : []).map((s) => (
            <div key={s.k}>
              <p className="font-display text-3xl md:text-4xl tracking-tight font-medium">{s.k}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.v}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-muted-foreground max-w-4xl mx-auto text-center px-6">{t("features.statsFootnote")}</p>
      </section>

      <section className="py-24" data-testid="features-grid">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 space-y-28">
          {sectionList.map((s, i) => {
            if (i === 7 && !isEnabled("hrModule")) return null
            let flag = null
            if (i === 2 && !isEnabled("stripeBilling")) flag = "stripeBilling"
            else if (i === 1 && !isEnabled("efacturaLive")) flag = "efacturaLive"
            else if (i === 4 && !isEnabled("telehealthGroupSessions")) flag = "telehealthGroupSessions"
            else if (i === 7 && !isEnabled("hrModule")) flag = "hrModule"
            const bullets = filterBullets(s.bullets, flag) || s.bullets
            return (
              <div key={s.title} id={i === sectionList.length - 1 ? "vertical-modules" : undefined} className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                <div className={`lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}>
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-4">
                    <s.icon strokeWidth={1.5} className="h-3.5 w-3.5 text-accent" />
                    {s.eyebrow}
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium leading-tight">{s.title}</h2>
                  <p className="mt-4 text-muted-foreground">{s.body}</p>
                  <ul className="mt-6 space-y-3">
                    {bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm">
                        <span className="mt-2 h-1 w-4 bg-accent rounded-full shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`lg:col-span-7 ${i % 2 ? "lg:order-1" : ""}`}>
                  <FeatureMock variant={i % 6} />
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {highlights.length > 0 && (
        <section className="border-t border-border py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 grid md:grid-cols-3 gap-8">
            {highlights.map((c, i) => {
              const Icon = HIGHLIGHT_ICONS[i] || Shield
              return (
                <div key={c.title} className="rounded-2xl border border-border p-7 bg-card">
                  <Icon strokeWidth={1.5} className="h-5 w-5 text-accent" />
                  <h3 className="font-display text-xl mt-4 font-medium">{c.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{c.body}</p>
                </div>
              )
            })}
          </div>
        </section>
      )}

      <section className="py-28 text-center">
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="font-display text-5xl tracking-tighter font-medium">{t("features.ctaTitle")}</h2>
          <p className="mt-4 text-muted-foreground">{t("features.ctaBody")}</p>
          <Link to="/signup">
            <Button size="lg" className="rounded-full mt-8 px-7 h-12">
              {t("common.startFreeTrial")} <ArrowRight strokeWidth={1.5} className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <p className="mx-auto max-w-5xl px-6 lg:px-10 pb-16 text-sm text-muted-foreground">
        <Link to="/integrations" className="text-accent hover:underline">{t("footer.integrations")}</Link>
        {" · "}
        <Link to="/security" className="text-accent hover:underline">{t("footer.security")}</Link>
        {" · "}
        <Link to="/pricing" className="text-accent hover:underline">{t("footer.pricing")}</Link>
      </p>
    </MarketingLayout>
  )
}

function FeatureMock({ variant }) {
  const { t } = useTranslation()
  const base = "relative rounded-2xl border border-border bg-card p-5 overflow-hidden"
  const m = (key, opts) => t(`features.mocks.${key}`, opts)

  if (variant === 0) {
    const names = t("features.mocks.calendarNames", { returnObjects: true })
    const nameList = Array.isArray(names) ? names : []
    return (
      <div className={base}>
        <div className="flex items-center justify-between mb-4">
          <p className="font-display text-lg">{m("calendarTitle")}</p>
          <span className="text-xs text-muted-foreground font-mono">{m("calendarRange")}</span>
        </div>
        <div className="grid grid-cols-5 gap-px bg-border rounded-xl overflow-hidden">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="bg-card h-16 relative">
              {CALENDAR_SLOTS.includes(i) && (
                <div className="absolute inset-1 rounded-md bg-secondary text-[10px] px-1.5 py-1">
                  {nameList[CALENDAR_SLOTS.indexOf(i)]}
                  <br />
                  <span className="text-muted-foreground">{m("calendarTime")}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (variant === 1) {
    return (
      <div className={base}>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">{m("soapHeader")}</p>
        <div className="space-y-3 text-sm">
          <p><span className="font-semibold">S:</span> {m("soapS")}</p>
          <p><span className="font-semibold">O:</span> {m("soapO")}</p>
          <p><span className="font-semibold">A:</span> {m("soapA")}</p>
          <p><span className="font-semibold">P:</span> {m("soapP")}</p>
        </div>
        <div className="mt-5 flex items-center gap-2 text-xs text-accent">
          <Sparkles strokeWidth={1.5} className="h-3.5 w-3.5" /> {m("copilotDraft")}
        </div>
      </div>
    )
  }

  if (variant === 2) {
    const rows = t("features.mocks.invoiceRows", { returnObjects: true })
    const list = Array.isArray(rows) ? rows : []
    const paidLabel = m("statusPaid")
    const unpaidLabel = m("statusUnpaid")
    return (
      <div className={base}>
        <div className="flex items-center justify-between mb-4">
          <p className="font-display text-lg">{m("invoicesTitle")}</p>
          <span className="text-xs font-mono text-muted-foreground">{m("invoicesMonth")}</span>
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border">
            {list.map((r) => {
              const isPaid = r.status === "paid"
              return (
                <tr key={r.id}>
                  <td className="py-2.5 font-mono text-xs text-muted-foreground">{r.id}</td>
                  <td>{r.client}</td>
                  <td className="text-right font-medium">{formatRon(r.amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                  <td className="text-right">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isPaid ? "bg-secondary" : "bg-accent/15 text-accent"}`}>
                      {isPaid ? paidLabel : unpaidLabel}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  if (variant === 3) {
    const timeline = t("features.mocks.timeline", { returnObjects: true })
    const items = Array.isArray(timeline) ? timeline : []
    return (
      <div className={base}>
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-full bg-secondary grid place-items-center font-medium">{m("clientInitials")}</div>
          <div>
            <p className="font-medium">{m("clientName")}</p>
            <p className="text-xs text-muted-foreground">{m("clientMeta")}</p>
          </div>
        </div>
        <div className="space-y-3 text-sm">
          {items.map((line, i) => (
            <div key={i} className="flex gap-3">
              <div className="h-2 w-2 rounded-full bg-accent mt-1.5" />
              <p className="text-muted-foreground">{line}</p>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (variant === 4) {
    return (
      <div className={base + " aspect-video grid place-items-center bg-gradient-to-br from-secondary/40 to-card"}>
        <div className="text-center">
          <div className="h-16 w-16 rounded-full bg-card border border-border grid place-items-center mx-auto">
            <Video strokeWidth={1.5} className="h-6 w-6 text-accent" />
          </div>
          <p className="mt-4 font-display text-lg">{m("telehealthTitle")}</p>
          <p className="text-xs text-muted-foreground font-mono mt-1">{m("telehealthMeta")}</p>
        </div>
      </div>
    )
  }

  const focusItems = t("features.mocks.focusItems", { returnObjects: true })
  const focusList = Array.isArray(focusItems) ? focusItems : []
  return (
    <div className={base}>
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">{m("focusTitle")}</p>
      <ul className="space-y-3 text-sm">
        {focusList.map((line, i) => (
          <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
            <Sparkles strokeWidth={1.5} className="h-3.5 w-3.5 text-accent mt-0.5 shrink-0" />
            {line}
          </li>
        ))}
      </ul>
    </div>
  )
}
